/* eslint-disable no-mixed-spaces-and-tabs */
import { MessageCircle, Minimize2, X } from 'lucide-react';
import { Dispatch, SetStateAction, useContext } from 'react';
import { Button } from '../../ui/button';
import { ConfigContext } from '@/config/config-context';
import { cn, isColorDark } from '@/lib/utils';

/**
 * ChatWindowTrigger component renders a button to expand or minimize the chat window.
 *
 * This component displays a button that toggles the chat window's expanded state.
 * It changes its icon and aria attributes based on the current state.
 *
 * @param {object} props - The properties object.
 * @param {boolean} props.expanded - A state indicating if the chat window is expanded.
 * @param {Dispatch<SetStateAction<boolean>>} props.setExpanded - A function to update the expanded state.
 * @param {Dispatch<SetStateAction<boolean>>} props.setChatWindowEnd - A function to update the chat window end state.
 * @returns {JSX.Element} The ChatWindowTrigger component.
 */
const ChatWindowTrigger = ({
	expanded,
	setExpanded,
	setChatWindowEnd,
}: {
	expanded: boolean;
	setExpanded: Dispatch<SetStateAction<boolean>>;
	setChatWindowEnd: Dispatch<SetStateAction<boolean>>;
}): JSX.Element => {
	const config = useContext(ConfigContext);
	return (
		<Button
			name={
				config?.expandChatText && !expanded
					? config?.expandChatText
					: config?.minimizedChatText && expanded
						? config?.minimizedChatText
						: undefined
			}
			variant='primary'
			className={`!wlcb-rounded-full ${expanded ? 'wlcb-bg-primary-pressed' : ''} ${isColorDark({ color: config?.theme?.primary?.color ?? '' }) ? 'wlcb-border-0' : 'wlcb-border wlcb-border-theme-brand-primary-hover'}`}
			size='lg'
			aria-label={expanded ? 'Minimera Chatfönster' : 'Expandera Chatfönster'}
			aria-controls='chat-window'
			aria-haspopup='dialog'
			aria-expanded={expanded}
			icon={{
				iconType:
					expanded && !config?.minimizedChatText ? (
						<Minimize2
							aria-hidden='true'
							className={cn('wlcb-size-4 ', {
								'wlcb-stroke-[var(--theme-brand-primary-text-color)]':
									config?.theme?.primary,
								'wlcb-stroke-white': !config?.theme?.primary,
							})}
						/>
					) : expanded && config?.minimizedChatText ? (
						<X
							aria-hidden='true'
							className={cn('wlcb-size-4 ', {
								'wlcb-stroke-[var(--theme-brand-primary-text-color)]':
									config?.theme?.primary,
								'wlcb-stroke-white': !config?.theme?.primary,
							})}
						/>
					) : config?.minimizedIcon && config?.minimizedIcon.trim() !== '' ? (
						<img
							src={config?.minimizedIcon}
							alt='minimized icon'
							aria-hidden='true'
							className='wlcb-size-4'
						/>
					) : (
						<MessageCircle
							aria-hidden='true'
							className={cn('wlcb-size-4', {
								'wlcb-stroke-[var(--theme-brand-primary-text-color)]':
									config?.theme?.primary,
								'wlcb-stroke-white': !config?.theme?.primary,
							})}
						/>
					),
				orientation: 'default',
			}}
			onClick={() => {
				setExpanded(!expanded), setChatWindowEnd(false);
			}}
		/>
	);
};

export default ChatWindowTrigger;
